import { Controller, useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@client/new/components/basic';
import { Chips } from '../primitives/Chips';
import { CollapsibleSection } from '../primitives/CollapsibleSection';
import { EquipmentField } from '../primitives/EquipmentField';
import { useSectionOpenState } from '../../lib/useSectionOpenState';
import * as styles from './AdvancedSection.css';
import {
  CATEGORY_LIMIT,
  CATEGORY_OPTIONS,
  DIFFICULTY_OPTIONS,
  EQUIPMENT_MAX_ITEMS,
  EQUIPMENT_MAX_LENGTH,
  LOCATION_OPTIONS,
  ProgramFormValues,
  TARGET_AREA_LIMIT,
  TARGET_AREA_OPTIONS,
} from '../../model/programForm.types';

export type AdvancedSectionProps = {
  isSubmitting?: boolean;
};

export const AdvancedSection = ({ isSubmitting }: AdvancedSectionProps) => {
  const { control } = useFormContext<ProgramFormValues>();
  const { isOpen, setIsOpen } = useSectionOpenState();

  return (
    <CollapsibleSection
      title="Advanced"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className={styles.fields}>
        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <div className={styles.selectField}>
              <Autocomplete
                multiple
                disableClearable
                options={CATEGORY_OPTIONS}
                value={field.value}
                onChange={(_, next) => {
                  if (next.length > CATEGORY_LIMIT) return;
                  field.onChange(next);
                }}
                disabled={isSubmitting}
                data-testid="program-form-tags"
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Category"
                    placeholder={
                      field.value.length === 0 ? 'Select' : undefined
                    }
                    helperText={`${field.value.length}/${CATEGORY_LIMIT} Selected`}
                  />
                )}
              />
              <Chips
                values={field.value}
                onRemove={(v) =>
                  field.onChange(field.value.filter((t) => t !== v))
                }
                disabled={isSubmitting}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="targetArea"
          render={({ field }) => (
            <div className={styles.selectField}>
              <Autocomplete
                multiple
                disableClearable
                options={TARGET_AREA_OPTIONS}
                value={field.value}
                onChange={(_, next) => {
                  if (next.length > TARGET_AREA_LIMIT) return;
                  field.onChange(next);
                }}
                disabled={isSubmitting}
                data-testid="program-form-target-area"
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Target Area"
                    placeholder={
                      field.value.length === 0 ? 'Select' : undefined
                    }
                    helperText={`${field.value.length}/${TARGET_AREA_LIMIT} Selected`}
                  />
                )}
              />
              <Chips
                values={field.value}
                onRemove={(v) =>
                  field.onChange(field.value.filter((t) => t !== v))
                }
                disabled={isSubmitting}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="requiredEquipment"
          render={({ field }) => (
            <div className={styles.equipmentField}>
              <div className={styles.label}>Equipment</div>
              <EquipmentField
                value={field.value}
                onChange={field.onChange}
                disabled={isSubmitting}
                placeholder="Write"
                maxLength={EQUIPMENT_MAX_LENGTH}
                maxItems={EQUIPMENT_MAX_ITEMS}
                hint={
                  <div className={styles.hint}>
                    Press Enter after typing to add new one
                  </div>
                }
                testId="program-form-equipment"
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="location"
          render={({ field }) => (
            <div className={styles.segmentedField}>
              <div className={styles.label}>Location</div>
              <ToggleButtonGroup
                exclusive
                value={field.value || null}
                onChange={(_, next) => field.onChange(next ?? '')}
                disabled={isSubmitting}
                data-testid="program-form-location"
              >
                {LOCATION_OPTIONS.map((option) => (
                  <ToggleButton key={option} value={option}>
                    {option}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          )}
        />

        <Controller
          control={control}
          name="difficulty"
          render={({ field }) => (
            <div className={styles.segmentedField}>
              <div className={styles.label}>Difficulty</div>
              <ToggleButtonGroup
                exclusive
                value={field.value || null}
                onChange={(_, next) => field.onChange(next ?? '')}
                disabled={isSubmitting}
                data-testid="program-form-difficulty"
              >
                {DIFFICULTY_OPTIONS.map((option) => (
                  <ToggleButton key={option} value={option}>
                    {option}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          )}
        />
      </div>
    </CollapsibleSection>
  );
};
